import React, { useState, useEffect, useCallback } from 'react';
import './OrganizationOverview.css';
import { Box, Typography, Button } from '@mui/material';
import DocumentLogo from '../../../../assets/Admin/document-logo.svg';
import KoinIcon from '../../../../assets/Admin/EarningIcons/koin-icons.svg';
import EditIcon from '@mui/icons-material/Edit';
import { useNavigate } from 'react-router-dom';
import { axiosInstance } from '../../../../utils/tokenUtils';
import { toast } from 'react-hot-toast';
import '../../SkeletonLoader/SkeletonLoader.css';

const OrganizationOverview = ({ organisationData, canManageOrganisations = false }) => {
  const navigate = useNavigate();
  const [earningsData, setEarningsData] = useState(null);
  const [earningsLoading, setEarningsLoading] = useState(true);
  const [earningsError, setEarningsError] = useState(null);
  const [conversionRate, setConversionRate] = useState(7.5); // Default fallback
  const [conversionLoading, setConversionLoading] = useState(true);

  // Fetch conversion rate
  const fetchConversionRate = useCallback(async () => {
    try {
      setConversionLoading(true);
      const response = await axiosInstance.get('/auth/koins-to-riyal');
      setConversionRate(response.data.conversion_rate);
    } catch (error) {
      console.error('Error fetching conversion rate:', error);
      // Keep default value of 7.5 if API fails
    } finally {
      setConversionLoading(false);
    }
  }, []);

  // Fetch earnings data
  const fetchEarningsData = useCallback(async () => {
    if (!organisationData?.id) {
      setEarningsLoading(false);
      return;
    }

    try {
      setEarningsLoading(true);
      setEarningsError(null);

      const response = await axiosInstance.get(
        `/admin/organisation/total-earnings/${organisationData.id}`
      );
      setEarningsData(response.data);
    } catch (error) {
      console.error('Error fetching earnings data:', error);
      setEarningsError('Failed to load earnings data');
      setEarningsData(null);
    } finally {
      setEarningsLoading(false);
    }
  }, [organisationData?.id]);

  useEffect(() => {
    fetchConversionRate();
  }, [fetchConversionRate]);

  useEffect(() => {
    fetchEarningsData();
  }, [fetchEarningsData]);

  if (!organisationData) return null;

  // Helper function to format date
  const formatDate = dateString => {
    if (!dateString) return '---';
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return '---';
      return `${String(date.getDate()).padStart(2, '0')}-${String(date.getMonth() + 1).padStart(2, '0')}-${date.getFullYear()}`;
    } catch (error) {
      return '---';
    }
  };

  // Helper function to format gender
  const formatGender = gender => {
    if (!gender) return '---';
    return gender.charAt(0).toUpperCase() + gender.slice(1);
  };

  // Convert koins to riyal
  const convertKoinsToRiyal = koins => {
    if (!koins) return 0;
    const numericKoins = parseFloat(koins);
    return isNaN(numericKoins) ? 0 : Math.round(numericKoins * conversionRate);
  };

  // Calculate koin values (assuming 1 Koin = $2 USD)
  const calculateKoins = amount => {
    if (!amount) return 0;
    const numericAmount = parseFloat(amount);
    return isNaN(numericAmount) ? 0 : Math.round(numericAmount / 2);
  };

  const handleEditClick = () => {
    if (!canManageOrganisations) {
      toast.dismiss();
      toast.error("You don't have permission to edit organization");
      return;
    }
    navigate(`/admin/organisation/edit/${organisationData.id || '1'}`);
  };

  return (
    <Box className="org-overview-container">
      {/* Lifetime Earning Section */}
      <Box className="org-overview-lifetime-earning-section">
        <Typography className="org-overview-lifetime-earning-title">Lifetime Earning</Typography>
        <Box className="org-overview-lifetime-earning-cards">
          {/* Total Earnings Card */}
          <Box className="org-overview-total-earning-card">
            <Box className="org-overview-earning-card-content">
              <Box className="org-overview-earning-card-header">
                <Typography className="org-overview-earning-card-label">Total Earnings</Typography>
              </Box>
              <Box style={{ display: 'flex', alignItems: 'center', flexDirection: 'row' }}>
                {/* <img
                  src={KoinIcon}
                  alt="Koin"
                  className="org-overview-koin-icon"
                  style={{ width: '25px', height: '25px', marginRight: '5px' }}
                /> */}

                <Typography className="org-overview-total-earning-value">
                  {earningsLoading ? (
                    <div className="org-earn-skeleton org-earn-skeleton--amount" />
                  ) : earningsError ? (
                    '---'
                  ) : (
                    `${convertKoinsToRiyal(earningsData?.lifetime_earnings || 0)} Riyal`
                  )}
                </Typography>
              </Box>
              <Box style={{ display: 'flex', alignItems: 'center', flexDirection: 'row' }}>
                <img src={KoinIcon} alt="Koin" className="org-overview-koin-icon" />
                <Typography className="org-overview-earning-card-subtitle">
                  {earningsLoading ? (
                    <div className="org-earn-skeleton org-earn-skeleton--koin" />
                  ) : earningsError ? (
                    '---'
                  ) : (
                    earningsData?.lifetime_earnings || 0
                  )}
                </Typography>
              </Box>
              <Typography className="org-overview-earning-card-subtitle">
                1 koin = {conversionRate} Riyals
              </Typography>
            </Box>
          </Box>

          {/* Vested Earning Card */}
          <Box className="org-overview-earning-card">
            <Box className="org-overview-earning-card-content">
              <Box className="org-overview-earning-card-header">
                <Typography className="org-overview-earning-card-label">Vested Earning</Typography>
              </Box>
              <Typography className="org-overview-earning-card-value">
                {earningsLoading ? (
                  <div className="org-earn-skeleton org-earn-skeleton--amount" />
                ) : earningsError ? (
                  '---'
                ) : (
                  `${convertKoinsToRiyal(earningsData?.vested_earnings || 0)} Riyal`
                )}
              </Typography>
              <Box style={{ display: 'flex', alignItems: 'center', flexDirection: 'row' }}>
                <img src={KoinIcon} alt="Koin" className="org-overview-koin-icon" />
                <Typography className="org-overview-earning-card-subtitle">
                  {earningsLoading ? (
                    <div className="org-earn-skeleton org-earn-skeleton--koin" />
                  ) : earningsError ? (
                    '---'
                  ) : (
                    earningsData?.vested_earnings || 0
                  )}
                </Typography>
              </Box>
            </Box>
          </Box>

          {/* Total Payout Card */}
          <Box className="org-overview-earning-card">
            <Box className="org-overview-earning-card-content">
              <Box className="org-overview-earning-card-header">
                <Typography className="org-overview-earning-card-label">Total Payout</Typography>
              </Box>
              <Typography className="org-overview-earning-card-value">
                {earningsLoading ? (
                  <div className="org-earn-skeleton org-earn-skeleton--amount" />
                ) : earningsError ? (
                  '---'
                ) : (
                  `${convertKoinsToRiyal(earningsData?.total_payout || 0)} Riyal`
                )}
              </Typography>
              <Box style={{ display: 'flex', alignItems: 'center', flexDirection: 'row' }}>
                <img src={KoinIcon} alt="Koin" className="org-overview-koin-icon" />
                <Typography className="org-overview-earning-card-subtitle">
                  {earningsLoading ? (
                    <div className="org-earn-skeleton org-earn-skeleton--koin" />
                  ) : earningsError ? (
                    '---'
                  ) : (
                    earningsData?.total_payout || 0
                  )}
                </Typography>
              </Box>
            </Box>
          </Box>

          {/* Available for Payout Card */}
          <Box className="org-overview-earning-card">
            <Box className="org-overview-earning-card-content">
              <Box className="org-overview-earning-card-header">
                <Typography className="org-overview-earning-card-label">
                  Available for Payout
                </Typography>
              </Box>
              <Typography className="org-overview-earning-card-value">
                {earningsLoading ? (
                  <div className="org-earn-skeleton org-earn-skeleton--amount" />
                ) : earningsError ? (
                  '---'
                ) : (
                  `${convertKoinsToRiyal(earningsData?.available_for_payout || 0)} Riyal`
                )}
              </Typography>
              <Box style={{ display: 'flex', alignItems: 'center', flexDirection: 'row' }}>
                <img src={KoinIcon} alt="Koin" className="org-overview-koin-icon" />
                <Typography className="org-overview-earning-card-subtitle">
                  {earningsLoading ? (
                    <div className="org-earn-skeleton org-earn-skeleton--koin" />
                  ) : earningsError ? (
                    '---'
                  ) : (
                    earningsData?.available_for_payout || 0
                  )}
                </Typography>
              </Box>
            </Box>
          </Box>
        </Box>
      </Box>

      {/* Details Section */}
      <Box className="org-overview-details-section">
        <Box className="org-overview-details-header">
          <Typography className="org-overview-details-title">Details</Typography>
          <Button className="org-overview-edit-button" onClick={handleEditClick}>
            Edit
          </Button>
        </Box>

        {/* Organization Details */}
        <Box className="org-overview-details-card">
          <Typography className="org-overview-details-subtitle">Organization</Typography>
          <Box className="org-overview-details-grid">
            <Box className="org-overview-detail-item">
              <Typography className="org-overview-detail-label">Name</Typography>
              <Typography className="org-overview-detail-value">
                {organisationData.name || '---'}
              </Typography>
            </Box>
            <Box className="org-overview-detail-item">
              <Typography className="org-overview-detail-label">Email</Typography>
              <Typography className="org-overview-detail-value">
                {organisationData.email || '---'}
              </Typography>
            </Box>
            <Box className="org-overview-detail-item">
              <Typography className="org-overview-detail-label">Contact Number</Typography>
              <Typography className="org-overview-detail-value">
                {organisationData.phone_no || '---'}
              </Typography>
            </Box>
            <Box className="org-overview-detail-item">
              <Typography className="org-overview-detail-label">Address</Typography>
              <Typography className="org-overview-detail-value">
                {organisationData.address || '---'}
              </Typography>
            </Box>
            <Box className="org-overview-detail-item">
              <Typography className="org-overview-detail-label">Country</Typography>
              <Typography className="org-overview-detail-value">
                {organisationData.country || '---'}
              </Typography>
            </Box>
            <Box className="org-overview-detail-item">
              <Typography className="org-overview-detail-label">City</Typography>
              <Typography className="org-overview-detail-value">
                {organisationData.city || '---'}
              </Typography>
            </Box>
            <Box className="org-overview-detail-item">
              <Typography className="org-overview-detail-label">Created On</Typography>
              <Typography className="org-overview-detail-value">
                {formatDate(organisationData.created_at) || '---'}
              </Typography>
            </Box>
          </Box>
        </Box>

        {/* Owner Details */}
        <Box className="org-overview-details-card">
          <Typography className="org-overview-details-subtitle">Owner</Typography>
          <Box className="org-overview-details-grid">
            <Box className="org-overview-detail-item">
              <Typography className="org-overview-detail-label">Name</Typography>
              <Typography className="org-overview-detail-value">
                {organisationData.owner_name || '---'}
              </Typography>
            </Box>
            <Box className="org-overview-detail-item">
              <Typography className="org-overview-detail-label">Contact Number</Typography>
              <Typography className="org-overview-detail-value">
                {organisationData.owner_phone_no || '---'}
              </Typography>
            </Box>
            <Box className="org-overview-detail-item">
              <Typography className="org-overview-detail-label">Gender</Typography>
              <Typography className="org-overview-detail-value">
                {formatGender(organisationData.owner_gender) || '---'}
              </Typography>
            </Box>
            <Box className="org-overview-detail-item">
              <Typography className="org-overview-detail-label">Date of Birth</Typography>
              <Typography className="org-overview-detail-value">
                {formatDate(organisationData.owner_dob) || '---'}
              </Typography>
            </Box>
          </Box>
        </Box>

        {/* Bank Details */}

        <Box className="org-overview-details-card">
          <Typography className="org-overview-details-subtitle">Bank Details</Typography>
          <Box className="org-overview-details-grid">
            <Box className="org-overview-detail-item">
              <Typography className="org-overview-detail-label">Bank Name</Typography>
              <Typography className="org-overview-detail-value">
                {organisationData.bank_details && organisationData.bank_details.bank_name
                  ? organisationData.bank_details.bank_name
                  : '---'}
              </Typography>
            </Box>
            <Box className="org-overview-detail-item">
              <Typography className="org-overview-detail-label">Bank Address</Typography>
              <Typography className="org-overview-detail-value">
                {organisationData.bank_details && organisationData.bank_details.bank_address
                  ? organisationData.bank_details.bank_address
                  : '---'}
              </Typography>
            </Box>
            <Box className="org-overview-detail-item">
              <Typography className="org-overview-detail-label">Account Holder Name</Typography>
              <Typography className="org-overview-detail-value">
                {organisationData.bank_details && organisationData.bank_details.account_holder_name
                  ? organisationData.bank_details.account_holder_name
                  : '---'}
              </Typography>
            </Box>
            <Box className="org-overview-detail-item">
              <Typography className="org-overview-detail-label">IBAN Number</Typography>
              <Typography className="org-overview-detail-value">
                {organisationData.bank_details && organisationData.bank_details.iban
                  ? organisationData.bank_details.iban
                  : '---'}
              </Typography>
            </Box>
          </Box>
        </Box>

        {/* Documents Uploaded */}
        <Box className="org-overview-details-card">
          <Typography className="org-overview-details-subtitle">Documents Uploaded</Typography>
          <Box className="org-overview-details-grid" style={{ gridTemplateColumns: '1fr' }}>
            <Box className="org-overview-detail-item">
              {/* <Typography className="org-overview-detail-label">Document Name</Typography> */}
              {organisationData.documents &&
              Array.isArray(organisationData.documents) &&
              organisationData.documents.length > 0 ? (
                organisationData.documents.map((doc, idx) => (
                  <a
                    key={idx}
                    href={doc.url}
                    className="org-overview-detail-value org-overview-document-link-text"
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ display: 'flex', alignItems: 'center', marginBottom: '8px' }}
                  >
                    <img src={DocumentLogo} alt="Document" className="org-overview-document-logo" />
                    {doc.type
                      ? doc.type.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())
                      : 'Document'}
                    <span className="org-overview-view-text">View</span>
                  </a>
                ))
              ) : (
                <Typography className="org-overview-detail-value">No document available</Typography>
              )}
            </Box>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default OrganizationOverview;
